import React, { useEffect,useCallback, useMemo, useRef, useState } from "react";
import {Sentence} from "../data/Sentences";
import {StoryBook} from "../data/StoryBooks";
import SentenceStore from "../data/SentenceStore";
import {Button} from "react-bootstrap";
import {Punishment, PUNISHMENTS} from "./SubPunishments";
import "./Fireworks.scss";
import {Link} from "react-router-dom";

function triggerHapticError() {
    if (!("vibrate" in navigator)) return;

    try {
        // Short pulse; iOS ignores patterns anyway
        navigator.vibrate(10);
    } catch {
        // ignore
    }
}

const COOLDOWN_PER_MISTAKE_SEC = 1.5;
const MAX_COOLDOWN_SEC = 20;

function pickPunishment(mistakeCount: number): Punishment {
    const eligible = PUNISHMENTS.filter(p =>
        mistakeCount >= p.minMistakes && mistakeCount <= p.maxMistakes
    );

    const pool = eligible.length > 0 ? eligible : PUNISHMENTS;

    const idx = Math.floor(Math.random() * pool.length);
    return pool[Math.min(idx, pool.length - 1)];
}

function pickResetPunishment(mistakeCount: number): Punishment {
    const eligible = PUNISHMENTS.filter(p =>
        p.forceRestart
    );

    const pool = eligible.length > 0 ? eligible : PUNISHMENTS;

    const idx = Math.floor(Math.random() * pool.length);
    return pool[Math.min(idx, pool.length - 1)];
}


function calcCooldownSec(mistakeCount: number): number {
    return Math.min(MAX_COOLDOWN_SEC, mistakeCount * COOLDOWN_PER_MISTAKE_SEC);
}


type StoryProgress = {
    version: 1;
    completedCount: number;        // how many sentences have been completed
    percentageComplete: number;    // 0..100
};

function storyProgressKey(storyPath: string) {
    return `typinggame:progress:v1:${encodeURIComponent(storyPath)}`;
}

function loadStoryProgress(storyPath: string): StoryProgress | null {
    try {
        const raw = localStorage.getItem(storyProgressKey(storyPath));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoryProgress;
        if (parsed?.version !== 1) return null;
        return parsed;
    } catch {
        return null;
    }
}

function saveStoryProgress(storyPath: string, progress: StoryProgress) {
    localStorage.setItem(storyProgressKey(storyPath), JSON.stringify(progress));
}

function clearStoryProgress(storyPath: string) {
    localStorage.removeItem(storyProgressKey(storyPath));
}




type WordInfo = { word: string; start: number; end: number }; // [start, end)

function getWords(sentence: string): WordInfo[] {
    const result: WordInfo[] = [];
    let index = 0;
    const parts = sentence.split(" ");

    for (const raw of parts) {
        const start = index;
        const end = start + raw.length;
        result.push({ word: raw, start, end });
        index = end + 1; // +1 for the space
    }

    return result;
}

function getCurrentWordIndex(words: WordInfo[], typedLength: number): number {
    for (let i = 0; i < words.length; i++) {
        const w = words[i];
        if (typedLength <= w.end) return i;
    }
    return words.length - 1;
}


type TypingGameProps = {
    story: StoryBook;
};

const TypingGame: React.FC<TypingGameProps> = ({ story }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [activePunishment, setActivePunishment] = useState<Punishment | null>(null);
    const [cooldownLeft, setCooldownLeft] = useState(0);
    const [storyCompleted, setStoryCompleted] = useState(false);


    type PendingCompletion = {
        scoreForSentence: number;
        strokesPerSecond: number;
        sentenceHtml: string;
        sentenceId: number;
        nextIndex: number;
        isLast: boolean;
    };

    const [pendingCompletion, setPendingCompletion] = useState<PendingCompletion | null>(null);


    const [sentences, setSentences] = useState<Sentence[]>([]);


    const [typed, setTyped] = useState("");
    const [strokes, setStrokes] = useState(0);

    // mistake tracking
    const [mistakes, setMistakes] = useState(0);        // number of wrong keystrokes
    const [wrongPenalty, setWrongPenalty] = useState(0); // total penalty points

    const [sentenceStartTime, setSentenceStartTime] = useState<number | null>(null);

    const [totalScore, setTotalScore] = useState(0);
    const [lastSentenceSpeed, setLastSentenceSpeed] = useState<number | null>(null);
    /*const [lastSentenceMistakes, setLastSentenceMistakes] = useState<number | null>(null);
    const [lastSentenceScore, setLastSentenceScore] = useState<number | null>(null);
    const [lastSentencePenalty, setLastSentencePenalty] = useState<number | null>(null);
    */
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [lastSentence, setLastSentence] = useState<string | null>(null);
    const [completedIndexes, setCompletedIndexes] = useState<number[]>([]);

    const [showStoryOverlay, setShowStoryOverlay] = useState(false);
    const [errorFlash, setErrorFlash] = useState(false);

    const persistProgress = useCallback((newCompletedCount: number) => {
        const pct = sentences.length
            ? Math.min(100, Math.max(0, (newCompletedCount / sentences.length) * 100))
            : 0;


        saveStoryProgress(story.storyPath, {
            version: 1,
            completedCount: newCompletedCount,
            percentageComplete: pct,
        });
    }, [sentences.length, story.storyPath]);


    useEffect(() => {
        if (!sentences.length) return;

        const progress = loadStoryProgress(story.storyPath);
        if (!progress) return;


        const completedCount = Math.max(0, Math.min(progress.completedCount, sentences.length));

        // Rebuild: sentence 1..(completedCount-1) in the log; resume at completedCount
        const completed = sentences.slice(0, Math.max(0, completedCount)).map(s => s.id);

        const isDone = completedCount >= sentences.length;
        setStoryCompleted(isDone);

        setCompletedIndexes(completed);
        setCurrentIndex(isDone ? Math.max(0, sentences.length - 1) : Math.min(completedCount, sentences.length - 1));
        setLastSentence(null);
        setTyped("");
        setStrokes(0);
        setMistakes(0);
        setWrongPenalty(0);
        setSentenceStartTime(Date.now());
    }, [sentences, story.storyPath]);

    useEffect(() => {
        if (!activePunishment) return;

        const endAt = Date.now() + cooldownLeft * 1000;
        const t = window.setInterval(() => {
            const left = Math.max(0, (endAt - Date.now()) / 1000);
            setCooldownLeft(left);
            if (left <= 0) window.clearInterval(t);
        }, 100);

        return () => window.clearInterval(t);
    }, [activePunishment, cooldownLeft]);



    useEffect(() => {
        let cancelled = false;

        setSentences([]);
        setCurrentIndex(0);

        SentenceStore.getSentences(story.storyPath).then((loaded) => {
            if (!cancelled) setSentences(loaded);
        });

        return () => {
            cancelled = true;
        };
    }, [story.storyPath]);

    var currentSentence: Sentence | null = sentences[currentIndex] ?? null;
    const plain = currentSentence?.plain ?? "";
    const words = useMemo(() => getWords(plain), [plain]);
    const currentWordIndex = getCurrentWordIndex(words, typed.length);

    useEffect(() => {
        if (sentenceStartTime === null) {
            setSentenceStartTime(Date.now());
        }
    }, [sentenceStartTime]);

    const resetForNextSentence = useCallback((nextIndex: number) => {
        setTyped("");
        setStrokes(0);
        setMistakes(0);
        if (storyCompleted) return;
        setCurrentIndex(nextIndex);
        setWrongPenalty(0);
        setSentenceStartTime(Date.now());
    }, [storyCompleted]);

    const triggerErrorFlash = useCallback(() => {
        setErrorFlash(true);
        setTimeout(() => setErrorFlash(false), 120);
    }, []);

    const isSentenceCompleted = useCallback((typed: string, target: string): boolean => {
        if (typed.length !== target.length) return false;
        return typed.toLowerCase() === target.toLowerCase();
    }, []);

    const completeSentence = useCallback(() => {
        if (sentenceStartTime === null) return;
        if (currentSentence == null) return;

        const durationSec = Math.max(0.01, (Date.now() - sentenceStartTime) / 1000);
        const strokesPerSecond = strokes / durationSec;
        const scoreForSentence = Math.max(0, strokesPerSecond - wrongPenalty);

        const nextIndex = currentIndex + 1;
        const isLast = currentIndex >= sentences.length - 1;

        // If no mistakes: proceed exactly as before
        if (mistakes === 0) {
            setTotalScore(prev => prev + scoreForSentence);
            setLastSentenceSpeed(strokesPerSecond);


            var sentence = currentSentence;
            setLastSentence(sentence.html);
            setCompletedIndexes(prev => {
                const next = [...prev, sentence.id];
                persistProgress(next.length);
                return next;
            });


            if (isLast) {
                persistProgress(sentences.length);
                setStoryCompleted(true);

                // Optional: prevent leftover typed chars
                setTyped("");
                setStrokes(0);
                setMistakes(0);
                setWrongPenalty(0);
                setSentenceStartTime(null);
                return;
            }

            resetForNextSentence(nextIndex);
            return;
        }

        // Mistakes > 0 => pause and show punishment
        const punishment = pickPunishment(mistakes);
        setActivePunishment(punishment);
        setCooldownLeft(calcCooldownSec(mistakes));

        setPendingCompletion({
            scoreForSentence,
            strokesPerSecond,
            sentenceHtml: currentSentence.html,
            sentenceId: currentSentence.id,
            nextIndex,
            isLast
        });
    }, [sentenceStartTime, currentSentence, strokes, wrongPenalty, currentIndex, sentences.length, mistakes, resetForNextSentence, persistProgress]);

    function TriggerPunishment() {
        const punishment = pickResetPunishment(mistakes);
        setActivePunishment(punishment);
        setCooldownLeft(calcCooldownSec(mistakes));
        setMistakes(0);
    }


    const handleKey = useCallback((rawChar: string) => {
        if (rawChar.length !== 1) return;
        if (currentSentence == null) return;
        if (activePunishment) return;
        if (storyCompleted) return;

        const char = rawChar;
        const target = currentSentence.plain;

        const nextIndex = typed.length;
        const expectedChar = target[nextIndex];

        setStrokes(prev => prev + 1);

        if (char.toLowerCase() === expectedChar.toLowerCase()) {
            const newTyped = typed + char;
            setTyped(newTyped);

            if (isSentenceCompleted(newTyped, target)) {
                completeSentence();
                setWrongPenalty(0);
            }
        } else {
            setMistakes(prev => prev + 1);
            triggerErrorFlash();
            triggerHapticError();
        }
    }, [
        typed,
        currentSentence,
        storyCompleted,
        activePunishment,
        isSentenceCompleted,
        completeSentence,
        triggerErrorFlash
    ]);

    // Listen for physical keyboard (no backspace)
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (showStoryOverlay) return;
            if (activePunishment) return;
            if (storyCompleted) return;


            if (e.key === "Enter") return;

            if (e.key === " " || e.key.length === 1) {
                e.preventDefault();
                const ch = e.key === " " ? " " : e.key;
                handleKey(ch);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [activePunishment, handleKey, showStoryOverlay, storyCompleted]);



    const onPunishmentContinue = useCallback(() => {
        if (!activePunishment) return;

        const restart = activePunishment.forceRestart;

        setActivePunishment(null);

        if (restart) {
            // Restart same sentence
            setPendingCompletion(null);
            resetForNextSentence(currentIndex);

        } else {
            // Commit the completion and advance
            if (pendingCompletion) {
                setTotalScore(prev => prev + pendingCompletion.scoreForSentence);
                setLastSentenceSpeed(pendingCompletion.strokesPerSecond);

                setLastSentence(pendingCompletion.sentenceHtml);
                setCompletedIndexes(prev => {
                    const next = [...prev, pendingCompletion.sentenceId];
                    persistProgress(next.length);
                    return next;
                });

                resetForNextSentence(pendingCompletion.nextIndex);
            } else {
                // fallback: just advance
                resetForNextSentence((currentIndex + 1) % sentences.length);
            }
            setPendingCompletion(null);
        }

        // Bring keyboard back on mobile
        setTimeout(() => inputRef.current?.focus(), 0);
    }, [activePunishment, pendingCompletion, persistProgress,resetForNextSentence, currentIndex, sentences.length]);


    if (!currentSentence && !storyCompleted) {
        return <div className="game-root">Loading…</div>;
    }

    return (
        <div className="game-root">
            <div className="game-inner">
                {lastSentence && (
                    <div
                        className="last-sentence fade-up"
                        dangerouslySetInnerHTML={{ __html: lastSentence }}
                    />
                )}
                {storyCompleted && (

                    <div className={`current-sentence ${errorFlash ? "error-flash" : ""}`}>
                        Your have completed the story!<br/>
                        I am so very proud of you.<br/>
                        You deserve a break. And a reward!
                        <div className="pyro">
                            <div className="before"></div>
                            <div className="after"></div>
                        </div>
                    </div>
                )}
                {!storyCompleted && (
                    <div className={`current-sentence ${errorFlash ? "error-flash" : ""}`}>
                    {words.map((w, wordIdx) => {
                        // group: [start, end) plus a trailing space if present
                        const groupStart = w.start;
                        const hasTrailingSpace =
                            w.end < plain.length && plain[w.end] === " ";
                        const groupEnd = hasTrailingSpace ? w.end + 1 : w.end;

                        const chars: React.ReactNode[] = [];

                        for (let idx = groupStart; idx < groupEnd; idx++) {
                            const ch = plain[idx];
                            const targetChar = ch;
                            const typedChar = typed[idx];
                            const isSpace = ch === " ";

                            let className = "char";

                            const wordIdx = words.findIndex(
                                w => idx >= w.start && idx < w.end
                            );
                            if (idx < typed.length) {
                                // already typed characters
                                if (typedChar && typedChar.toLowerCase() === targetChar.toLowerCase()) {
                                    className += " char-correct";
                                } else {
                                    className += " char-wrong";
                                }
                            } else {
                                // not typed yet
                                if (isSpace) {
                                    className += " char-space";
                                } else {
                                    if (wordIdx === currentWordIndex) {
                                        // the word you're currently typing (un-typed tail)
                                        className += " char-current-untyped";
                                    } else if (wordIdx === currentWordIndex + 1) {
                                        // next word: slightly darker, will brighten when it becomes current
                                        className += " char-next";
                                    } else {
                                        // future words: invisible; will fade in when they become .char-next
                                        className += " char-future";
                                    }
                                }
                            }
                            const isTyped = idx < typed.length;
                            const isCaret = idx === typed.length;


                            const displayChar =
                                isSpace
                                    ? (isTyped ? "\u00A0" : (isCaret ? "\u00B7" : "\u00A0")) // typed => space, next space => dot, future spaces => invisible space
                                    : ch;

                            if (isCaret && isSpace) className += " char-space-caret";


                            chars.push(
                                <span key={idx} className={className}>
          {displayChar}
        </span>
                            );
                        }

                        // Wrap all chars of this word (plus trailing space) in one non-breaking span
                        return (
                            <span key={wordIdx} className="word">
        {chars}
      </span>
                        );
                    })}
                </div>
                )}

                <div className="hud">
                    <span>Score: <b>{totalScore.toFixed(1)}</b></span> &nbsp;
                    {lastSentenceSpeed !== null && (
                        <>
                            <span>Last speed: <b>{lastSentenceSpeed.toFixed(2)} k/s</b></span>
                            &nbsp;
                        </>
                    )}
                    {mistakes !== 0 && (
                        <p>Mistakes: <b className="text-danger">{mistakes}</b></p>
                    )}
                </div>

                <Button
                    className="story-button btn-dark btn-outline-danger"
                    onClick={() => setShowStoryOverlay(true)}
                >
                    📝
                </Button>


                {mistakes !== 0 && (
                <Button
                    className="punishment-button btn-dark btn-outline-danger"
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => {
                        const ok = window.confirm(
                            "Are you sure you want to trigger the punishment now?\nThis will restart the sentence, but also reset your mistakes.."
                        );
                        if (!ok) return;

                        TriggerPunishment();
                    }}
                >
                    🏓
                </Button>
                )}

                {!keyboardOpen && !storyCompleted && (
                    <button
                        className="keyboard-toggle"
                        onClick={() => inputRef.current?.focus()}
                    >
                        Tap here to type
                    </button>
                )}

                <input
                    ref={inputRef}
                    className="hidden-typing-input"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value=""
                    onChange={() => {
                        // we don’t care about the actual text in this input
                    }}
                    onFocus={() => setKeyboardOpen(true)}
                    onBlur={() => setKeyboardOpen(false)}
                    onKeyDown={(e) => {
                        if (showStoryOverlay) return;
                        if (activePunishment) return;

                        // We want exactly the same behavior as before:
                        if (e.key === "Enter") return;
                    }}
                />
            </div>

            {activePunishment && (
                <div className="punishment-overlay">
                    <div className="punishment-content">
                        <div className="punishment-title">Punishment</div>

                        <div className="punishment-message">{activePunishment.message}</div>

                        <div className="punishment-sub">
                            Mistakes: <b>{mistakes}</b>
                            {activePunishment.forceRestart ? " • You must restart this sentence." : " • You may continue."}
                        </div>

                        <Button
                            className="punishment-continue d-block btn-danger"
                            disabled={cooldownLeft > 0}
                            tabIndex={-1}
                            onPointerDown={(e) => {
                                // Prevent the button from taking focus (keeps keyboard up)
                                e.preventDefault();
                            }}
                            onClick={() => {
                                onPunishmentContinue();

                                // Re-focus as part of the same user interaction
                                requestAnimationFrame(() => inputRef.current?.focus());
                            }}
                        >
                            {cooldownLeft > 0 ? `Continue (${Math.ceil(cooldownLeft)}s)` : "Continue"}
                        </Button>
                    </div>
                </div>
            )}


            {showStoryOverlay && (
                <div className="overlay">
                    <div className="overlay-content">

                        <Link
                            to="/"
                            className="btn btn-dark"
                        >
                            &lt; To story library
                        </Link>
                        <button
                            className="overlay-close"
                            onClick={() => setShowStoryOverlay(false)}
                        >
                            Close
                        </button>

                        {sentences.filter(s =>
                            completedIndexes.includes(s.id)
                        ).map(s => (
                            <p
                                key={s.id}
                                dangerouslySetInnerHTML={{ __html: s.html }}
                            />
                        ))}

                        <Button
                            className="overlay-reset btn-danger"
                            onClick={() => {
                                clearStoryProgress(story.storyPath);

                                setCompletedIndexes([]);
                                setLastSentence(null);

                                resetForNextSentence(0); // resets typed/mistakes/etc and sets currentIndex
                                setShowStoryOverlay(false);

                                setTimeout(() => inputRef.current?.focus(), 0);
                            }}
                        >
                            Reset progress
                        </Button>

                    </div>
                </div>
            )}
        </div>
    );
};
export default TypingGame
